import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Button,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import {
  GetApp as GetAppIcon,
  HelpOutline as HelpOutlineIcon
} from '@material-ui/icons';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import WrappedTableCell from '../../common/WrappedTableCell';
import ConfirmDialog from '../../common/dialogs/ConfirmDialog';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateHeader from '../../common/page-template/PageTemplateHeader';
import PageTemplateCardOver from '../../common/page-template/PageTemplateCardOver';
import { FileDto } from '../documents/Document.interface';
import DocumentDownloadButton from '../documents/components/DocumentDownloadButton';
import DocumentTemplateDialog from '../documents/components/DocumentTemplateDialog';
import ReferenceDialog from '../reference/ReferenceDialog';
import {
  CapaReference,
  ChangeOrderReference,
  DocumentApprovalReference,
  EmployeeTrainingReference,
  RiskReference,
  TrainingPlanReference
} from '../reference/template.reference';
import {
  useCreateTemplate,
  useDownloadTemplate,
  useFindTemplates,
  useUpdateTemplate
} from './Template.hooks';
import { Template, TemplateType, TemplateTypeName } from './Template.interface';

function getTemplateReference(type: TemplateType) {
  switch (type) {
    case TemplateType.Capa:
      return CapaReference;
    case TemplateType.DocumentApproval:
      return DocumentApprovalReference;
    case TemplateType.Trainee:
      return EmployeeTrainingReference;
    case TemplateType.Risk:
      return RiskReference;
    case TemplateType.ChangeOrder:
      return ChangeOrderReference;
    case TemplateType.TrainingPlan:
      return TrainingPlanReference;
  }
}

const StyledTableCell = styled(TableCell)`
  .MuiButtonBase-root {
    margin-right: 8px;
  }
`;

export type TemplateItem = Pick<TemplateListItemProps, 'template' | 'type'>;

interface SelectedItem {
  item: TemplateItem;
  action: 'assign' | 'clear';
}

const TemplateListPage: React.FC = () => {
  const [error, setError] = useState<FetchError | undefined>();
  const [selected, setSelected] = useState<SelectedItem | undefined>();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [{ data, isLoading: isFinding, error: findError }, get] =
    useFindTemplates();
  const [createResult, create] = useCreateTemplate();
  const [updateResult, update] = useUpdateTemplate(
    selected?.item.template?.id || 0
  );
  const isLoading: boolean =
    isFinding || createResult.isLoading || updateResult.isLoading;
  const assignError: FetchError | undefined =
    createResult.error || updateResult.error;

  const items: TemplateItem[] = useMemo(() => {
    return Object.values(TemplateType).map((type: TemplateType) => {
      return {
        type,
        template: templates?.find(
          (template: Template) => template.type === type
        )
      };
    });
  }, [templates]);

  const clearAction = () => setSelected(undefined);

  // add or replace existing Template
  const addTemplate = (template: Template) => {
    // find existing template
    const index: number = templates.findIndex(
      (t: Template) => t.id === template.id
    );
    const newTemplates: Template[] = [...templates];
    if (index > -1) {
      newTemplates.splice(index, 1, template);
    } else {
      newTemplates.push(template);
    }
    setTemplates(newTemplates);
  };

  useEffect(() => {
    get();
  }, [get]);

  useEffect(() => {
    data && setTemplates(data);
  }, [data]);

  useEffect(() => {
    createResult.data && addTemplate(createResult.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createResult.data]);

  useEffect(() => {
    updateResult.data && addTemplate(updateResult.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateResult.data]);

  useEffect(() => {
    setError(findError);
  }, [findError]);

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <h2>Manage Templates</h2>
      </PageTemplateHeader>
      <PageTemplateCardOver>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Template</TableCell>
                <TableCell width={1}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {items.map((item: TemplateItem) => (
                <TemplateListItem
                  {...item}
                  key={item.type}
                  onError={setError}
                  onClear={() => setSelected({ item, action: 'clear' })}
                  onSelect={() => setSelected({ item, action: 'assign' })}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PageTemplateCardOver>

      <DocumentTemplateDialog
        hideUnsetButton
        open={selected?.action === 'assign'}
        file={selected?.item.template?.file}
        error={assignError}
        isLoading={isLoading}
        confirmLabel='Assign'
        title={`Assign ${
          selected ? TemplateTypeName[selected.item.type] : 'Document'
        } Template`}
        setOpen={(open: boolean) => !open && clearAction()}
        onSelectFile={(file?: FileDto) => {
          clearAction();
          if (!selected) {
            return;
          }
          const { type, template } = selected.item;
          const fileId: number | null = file?.id || null;
          if (!template) {
            create({ fileId, type });
          } else {
            update({ id: template.id, fileId });
          }
        }}
      />

      <ConfirmDialog
        open={selected?.action === 'clear'}
        title='Clear Document Template'
        message={`Are you sure you want to clear ${
          selected ? TemplateTypeName[selected.item.type] : 'this'
        } template?`}
        onCancel={clearAction}
        onConfirm={() => {
          clearAction();
          if (selected?.item.template) {
            update({ id: selected.item.template.id, fileId: null });
          }
        }}
      />

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'An error occurred.'}
      </AppSnackbar>
    </PageTemplate>
  );
};

export default TemplateListPage;

export interface TemplateListItemProps {
  type: TemplateType;
  template?: Template;
  onClear?: () => void;
  onSelect?: () => void;
  onError?: (error: FetchError) => void;
}

export const TemplateListItem: React.FC<TemplateListItemProps> = (
  props: TemplateListItemProps
) => {
  const { type, template, onClear, onSelect, onError } = props;
  const [referenceType, setReferenceType] = useState<TemplateType>();
  const [{ isLoading, error, response }, getDocument, downloadDocument] =
    useDownloadTemplate(type);
  const { file } = template || {};
  const label: string = TemplateTypeName[type];
  const storageKey: string | undefined =
    file?.fileVersion?.storageKey ?? file?.storageKey;
  const templateReference = referenceType
    ? getTemplateReference(referenceType)
    : undefined;

  useEffect(() => {
    if (response?.ok) {
      downloadDocument(`${label}.docx`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label, response]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <TableRow>
      <TableCell>{label}</TableCell>
      <WrappedTableCell>
        {file?.path ? (
          <Link to={`/portal/documents/file/task/${file.taskId}`}>
            {file.path}
          </Link>
        ) : (
          <Typography variant='body2' color='textSecondary'>
            Default
          </Typography>
        )}
      </WrappedTableCell>
      <StyledTableCell>
        {storageKey ? (
          <DocumentDownloadButton title={file?.name} storageKey={storageKey} />
        ) : (
          <Button
            color='primary'
            variant='contained'
            disabled={isLoading}
            endIcon={
              isLoading ? (
                <CircularProgress color='primary' size={20} />
              ) : (
                <GetAppIcon />
              )
            }
            onClick={() => getDocument()}
          >
            Download
          </Button>
        )}

        <Button color='primary' variant='contained' onClick={onSelect}>
          Select
        </Button>
        <Button
          color='primary'
          variant='contained'
          disabled={!file}
          onClick={onClear}
        >
          Clear
        </Button>
        <Button
          color='primary'
          variant='contained'
          endIcon={<HelpOutlineIcon />}
          disabled={!getTemplateReference(type)}
          onClick={() => setReferenceType(type)}
        >
          Reference
        </Button>
      </StyledTableCell>
      {referenceType && templateReference && (
        <ReferenceDialog
          helpMessage='Help'
          type={templateReference}
          open={!!templateReference}
          title={TemplateTypeName[referenceType]}
          onClose={() => setReferenceType(undefined)}
        />
      )}
    </TableRow>
  );
};
